import { inject, Injectable } from "@angular/core";
import { Data } from "@angular/router";
import { UtilityService } from "@core/utility.service";
import { TranslateLoader } from "@ngx-translate/core";
import { getApp } from "firebase/app";
import { doc, getDoc, getFirestore, loadBundle } from "firebase/firestore";
import * as JSZip from "jszip";
import { BehaviorSubject, from, Observable, of } from "rxjs";
import { catchError, filter, shareReplay, switchMap, take } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { CommonVariable } from "../app.constants";
import { LoggingService } from "./logging.service";

@Injectable({
  providedIn: "root",
})
export class TabsFirestoreService implements TranslateLoader {
  protected utilityService = inject(UtilityService);
  protected loggingService = inject(LoggingService);
  private validLanguages: Set<string> = new Set();
  private languageListLoaded$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.utilityService.getLanguageList().pipe(take(1)).subscribe(
      (l: Data) => {
        if (l?.languages?.length) {
          this.validLanguages = new Set(
            l.languages.filter(lang => lang.language_status).map(lang => lang.language_code)
          );
        }
        this.languageListLoaded$.next(true);
      },
      () => this.languageListLoaded$.next(true)
    );
  }

  getTranslation(lang: any): Observable<Data | null> {
    let selectedLang = "en";

    if (lang && typeof lang === "object") {
      selectedLang = lang["language_code"];
    } else if (lang && typeof lang === "string") {
      selectedLang = lang;
    }

    if (selectedLang.includes("lan")) {
      selectedLang = "en";
    }

    return this.languageListLoaded$.pipe(
      filter(loaded => loaded),
      take(1),
      switchMap(() => {
        if (this.validLanguages.size && !this.validLanguages.has(selectedLang)) {
          selectedLang = "en";
        }

        const currentTranslation = CommonVariable.tabsTranslation$?.value;
        if (
          currentTranslation?.lang === selectedLang &&
          Object.keys(currentTranslation.data || {}).length
        ) {
          return of(currentTranslation.data);
        }

        return this.returnTranslatedData(selectedLang);
      }),
      catchError((err) => {
        this.loggingService.logError(`[getTranslation] Error loading translation for "${selectedLang}":`, err);
        return of({});
      })
    );
  }

  private returnTranslatedData(lang: string): Observable<Data | null> {
    return from(this.fetchTranslatedData(lang)).pipe(
      switchMap(data => {
        if (data) {
          CommonVariable.tabsTranslation$.next({ lang, data });
        }
        return of(data ?? null);
      }),
      catchError(() => of(null)),
      shareReplay(1)
    );
  }

  private async fetchTranslatedData(lang: string, retry = false): Promise<Data> {
    try {
      const cdnUrl = environment.tabsTranslationCDNUrl?.[lang];
      let bundleText: string | null = null;

      // Try CDN first
      if (cdnUrl) {
        try {
          const response = await fetch(cdnUrl);
          if (response?.ok) {
            bundleText = await response.text();
          } else {
            this.loggingService.logError(`[fetchTranslatedData] CDN response not ok (${response?.status}) for "${lang}"`);
          }
        } catch (cdnError) {
          this.loggingService.logError(`[fetchTranslatedData] CDN request failed for "${lang}":`, cdnError);
        }
      }

      // If CDN failed, try remote bundle
      if (!bundleText) {
        try {
          bundleText = await this.getRemoteBundle(lang);
        } catch (remoteError) {
          this.loggingService.logError(`[fetchTranslatedData] Remote bundle error for "${lang}":`, remoteError);
        }
      }

      // If we have bundle text, load it into Firestore
      if (bundleText) {
        try {
          const app = getApp();
          const db = getFirestore(app);

          await loadBundle(db, bundleText);

          const ref = doc(db, "translations_main_tabs", lang);
          const snapshot = await getDoc(ref);

          if (snapshot.exists()) {
            const data = snapshot.data() as Data;
            if (data && Object.keys(data).length > 0) {
              return data;
            } else {
              this.loggingService.logError(`[fetchTranslatedData] Bundle loaded but no valid data found for "${lang}"`);
            }
          } else {
            this.loggingService.logError(`[fetchTranslatedData] Document does not exist in Firestore for "${lang}"`);
          }
        } catch (bundleError) {
          this.loggingService.logError(`[fetchTranslatedData] Bundle loading error for "${lang}":`, bundleError);
        }
      }

      // Retry once if this is the first attempt
      if (!retry) {
        this.loggingService.logError(`[fetchTranslatedData] Retrying fetch for "${lang}" (second attempt)...`);
        await this.delay(1000);
        return this.fetchTranslatedData(lang, true);
      }

      // Final fallback - return empty object
      this.loggingService.logError(`[fetchTranslatedData] All attempts failed for "${lang}", returning empty object`);
      return {};
    } catch (error) {
      this.loggingService.logError(`[fetchTranslatedData] Unexpected error for "${lang}":`, error);

      // Retry once if this is the first attempt
      if (!retry) {
        this.loggingService.logError(`[fetchTranslatedData] Retrying after unexpected error for "${lang}"`);
        await this.delay(1000);
        return this.fetchTranslatedData(lang, true);
      }
      return null;
    }
  }

  private async getRemoteBundle(lang: string, retry = false): Promise<string> {
    const zipUrl = environment.tabsTranslationRemoteBundleUrl as any;

    try {
      const bundleText = await this.extractZipData(zipUrl, lang);

      if (!bundleText) {
        if (!retry) {
          this.loggingService.logError(`[getRemoteBundle] Remote bundle failed, retrying for "${lang}"`);
          await this.delay(1000);
          return this.getRemoteBundle(lang, true);
        } else {
          this.loggingService.logError(`[getRemoteBundle] Remote bundle failed after retry, trying local bundle for "${lang}"`);
          return await this.getLocalBundle(lang);
        }
      }

      return bundleText;
    } catch (error) {
      this.loggingService.logError(`[getRemoteBundle] Error for "${lang}":`, error);

      if (!retry) {
        this.loggingService.logError(`[getRemoteBundle] Retrying after error for "${lang}"`);
        await this.delay(1000);
        return this.getRemoteBundle(lang, true);
      } else {
        this.loggingService.logError(`[getRemoteBundle] Falling back to local bundle after retry for "${lang}"`);
        return await this.getLocalBundle(lang);
      }
    }
  }

  private async getLocalBundle(lang: string): Promise<string | null> {
    try {
      const paths = {
        de: "../../assets/main-tabs-translates-zips/bundles_mainTabsData_de.zip",
        en: "../../assets/main-tabs-translates-zips/bundles_mainTabsData_en.zip",
        es: "../../assets/main-tabs-translates-zips/bundles_mainTabsData_es.zip",
        fr: "../../assets/main-tabs-translates-zips/bundles_mainTabsData_fr.zip",
        it: "../../assets/main-tabs-translates-zips/bundles_mainTabsData_it.zip",
        hi: "../../assets/main-tabs-translates-zips/bundles_translations_main_tabs_hi.zip",
        pt: "../../assets/main-tabs-translates-zips/bundles_translations_main_tabs_pt.zip",
      };

      return await this.extractZipData(paths?.[lang], lang);
    } catch (error) {
      this.loggingService.logError(`[getLocalBundle] Error loading local bundle for "${lang}":`, error);
      return null;
    }
  }

  private async extractZipData(zipUrl: string, lang: string): Promise<string | null> {
    try {
      if (!zipUrl) {
        this.loggingService.logError(`[extractZipData] No ZIP URL provided for "${lang}"`);
        return null;
      }

      const response = await fetch(zipUrl);
      if (!response.ok) {
        this.loggingService.logError(`[extractZipData] ZIP not found at "${zipUrl}" (${response.status}) for "${lang}"`);
        return null;
      }

      const data = await response.arrayBuffer();
      const zip = await JSZip.loadAsync(data);
      const bundleFile = zip.file("bundleData.txt");

      if (!bundleFile) {
        this.loggingService.logError(`[extractZipData] bundleData.txt not found in ZIP for "${lang}"`);
        return null;
      }

      const text = await bundleFile.async("string");
      return text || null;
    } catch {
      return null;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
